import React, { useEffect, useId, useRef, useState } from 'react';
import './DoutoFeatureCard.css';
import { useLocale } from './LocaleProvider';

// Por local (placement), não por site inteiro: fechar o card na home não deve apagar o
// mesmo convite no dashboard — cada ponto de contato é a própria chance de conversão.
const dismissKey = (placement) => `minimental:douto-promo-dismissed-until:${placement}`;
const dismissEvent = (placement) => `minimental:douto-promo-dismissed:${placement}`;
const DISMISS_DAYS = 7;

function linkFor(placement, action) {
  const url = new URL(action === 'learn' ? 'https://douto.app/' : 'https://dash.douto.app/');
  url.search = new URLSearchParams({
    utm_source: 'minimental', utm_medium: 'referral',
    utm_campaign: 'douto_assinatura', utm_content: placement + '_' + action,
    ...(action === 'subscribe' ? { plano: 'mensal' } : {}),
  }).toString();
  return url.toString();
}

function trackingContext(placement, variant) {
  return {
    placement,
    variant,
    audience: placement === 'dashboard' ? 'authenticated' : 'public',
    utm_source: 'minimental',
    utm_medium: 'referral',
    utm_campaign: 'douto_assinatura',
  };
}

function clickAttribution(placement, action, variant) {
  return {
    ...trackingContext(placement, variant),
    action,
    utm_content: placement + '_' + action,
    destination: action === 'learn' ? 'douto.app' : 'dash.douto.app',
    plan: action === 'subscribe' ? 'unlimited' : undefined,
    billing_interval: action === 'subscribe' ? 'monthly' : undefined,
  };
}

export default function DoutoFeatureCard({ placement = 'calculator_result', variant = 'inline' }) {
  const { locale } = useLocale();
  const es = locale === 'es';
  const [visible, setVisible] = useState(true);
  const titleId = useId();
  const cardRef = useRef(null);
  const impressionSent = useRef(false);
  const sidebar = variant === 'sidebar';

  useEffect(() => {
    const key = dismissKey(placement);
    const event = dismissEvent(placement);
    const sync = () => {
      try { setVisible(Number(window.localStorage.getItem(key) || 0) < Date.now()); }
      catch { /* The banner remains usable without storage. */ }
    };
    const hide = () => setVisible(false);
    sync();
    window.addEventListener(event, hide);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(event, hide);
      window.removeEventListener('storage', sync);
    };
  }, [placement]);

  useEffect(() => {
    const card = cardRef.current;
    if (!card || !visible || impressionSent.current || !('IntersectionObserver' in window)) return undefined;
    let timer;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
        if (!timer && !impressionSent.current) timer = window.setTimeout(() => {
          impressionSent.current = true;
          window.eidocAnalytics?.capture('douto_promo_viewed', trackingContext(placement, variant));
          timer = undefined;
        }, 1000);
      } else {
        window.clearTimeout(timer);
        timer = undefined;
      }
    }, { threshold: [0, 0.5, 1] });
    observer.observe(card);
    return () => { observer.disconnect(); window.clearTimeout(timer); };
  }, [visible, placement, variant]);

  function dismiss() {
    try { window.localStorage.setItem(dismissKey(placement), String(Date.now() + DISMISS_DAYS * 86400000)); }
    catch { /* Synchronize the current page even without storage. */ }
    window.dispatchEvent(new Event(dismissEvent(placement)));
    window.eidocAnalytics?.capture('douto_promo_dismissed', trackingContext(placement, variant));
    setVisible(false);
  }

  function cta(action, label, className) {
    return <a href={linkFor(placement, action)} target="_blank" rel="noopener noreferrer"
      className={className} onClick={() => window.eidocAnalytics?.capture('douto_promo_clicked', clickAttribution(placement, action, variant))}>
      {label}<span aria-hidden="true">↗</span><span className="sr-only">{es ? ' (abre en una pestaña nueva)' : ' (abre em nova aba)'}</span>
    </a>;
  }

  if (!visible) return null;

  return (
    <aside ref={cardRef} aria-labelledby={titleId} className={'douto-card douto-card--' + variant + ' no-print'}>
      <button type="button" onClick={dismiss} aria-label={es ? 'Ocultar la presentación de Douto durante 7 días' : 'Fechar apresentação do Douto por 7 dias'} className="douto-close">×</button>
      <div className="douto-copy">
        <div className="douto-brand"><img className="douto-logo" src="/douto-logo-144.webp" width="72" height="72" alt="" /><span>Douto</span></div>
        <p className="douto-eyebrow">{es ? 'Para profesionales de salud · por Ei, Doc!' : 'Para quem atende · por Ei, Doc!'}</p>
        <h2 id={titleId}>{sidebar ? (es ? <>¿Una duda clínica?<br /><em>Consúltala con Douto.</em></> : <>Uma dúvida clínica?<br /><em>Consulte o Douto.</em></>) : placement === 'article_inline' ? (es ? <>De la lectura a la práctica.<br /><em>Consulta tu duda con Douto.</em></> : <>Da leitura à prática.<br /><em>Leve sua dúvida ao Douto.</em></>) : (es ? <>La puntuación es el comienzo.<br /><em>El caso continúa.</em></> : <>O escore é o começo.<br /><em>O caso continua.</em></>)}</h2>
        <p className="douto-description">{sidebar
          ? (es ? 'Consulta posibilidades, alertas y referencias para el caso, directamente en el navegador.' : 'Consulte possibilidades, alertas e referências para o caso — direto no navegador.')
          : (es ? '¿Necesitas revisar una conducta? Describe el caso en Douto y consulta posibilidades, alertas y referencias con límites explícitos.' : 'Precisa conferir uma conduta? Descreva o caso no Douto e consulte possibilidades, alertas e referências, com limites explícitos.')}</p>
        {!sidebar && <ul className="douto-benefits">{es ? <><li>Sin instalar nada</li><li>Contexto en la misma conversación</li><li>Creado por un médico</li></> : <><li>Sem instalar nada</li><li>Contexto na mesma conversa</li><li>Feito por médico</li></>}</ul>}
        {cta('learn', es ? 'Conoce cómo funciona' : 'Veja como funciona', 'douto-learn')}
      </div>
      <div className="douto-offer">
        <p className="douto-offer-label">{es ? 'Para profesionales de salud' : 'Para profissionais de saúde'}</p>
        <p className="douto-trial">{es ? 'Primer caso gratis + 7 días de garantía' : 'Primeiro caso grátis + 7 dias de garantia'}</p>
        {cta('trial', es ? 'Enviar mi primer caso gratis' : 'Enviar meu primeiro caso grátis', 'douto-primary')}
        <p className="douto-terms">{es ? 'Cancela cuando quieras.' : 'Cancele quando quiser.'}</p>
        {es && <p className="douto-terms">Douto está disponible actualmente en portugués.</p>}
      </div>
      <p className="douto-disclaimer">{es ? 'Apoyo clínico para profesionales. Revisa las fuentes. No envíes datos que identifiquen a pacientes.' : 'Apoio clínico para profissionais. Confira as fontes. Não envie dados que identifiquem pacientes.'}</p>
    </aside>
  );
}
